import { NgxMatDatetimePickerModule, NgxMatNativeDateModule, NgxMatTimepickerModule } from '@angular-material-components/datetime-picker';
import { HttpClientModule } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ScheduleFrequency } from 'src/app/enums/email.enum';
import { ScheduleEmailProps } from 'src/app/pages/group-analytics/group-analytics-email-pricing-dialog/schedule-email/schedule-email';
import { AutocompleteSearchComponent } from 'src/app/shared/components/autocomplete-search/autocomplete-search.component';
import { TooltipListPipe } from 'src/app/shared/pipes/tooltip/tooltip-list.pipe';
import { SnackBarService } from 'src/app/ui-services/snackbar/snackBar.service';
import { TimezoneService } from 'src/app/helpers/datetime/timezone.service';
import { ScheduleEmailValidationService, ValidationState } from './schedule-email-validation.service';

@Component({
  selector: 'app-schedule-email',
  templateUrl: './schedule-email.component.html',
  styleUrls: ['./schedule-email.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatChipsModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatButtonModule,
    NgxMatTimepickerModule,
    NgxMatDatetimePickerModule,
    NgxMatNativeDateModule,
    MatTooltipModule,
    TooltipListPipe,
    AutocompleteSearchComponent
  ] 
})
export class ScheduleEmailComponent implements OnInit, OnChanges {
  @Input() tagList: string[] = [];
  @Input() recipientList: any[] = [];
  @Input() frequency: ScheduleFrequency = ScheduleFrequency.Daily;
  @Input() scheduleDateTime: Date = new Date();
  @Input() timezone: string = '';
  @Input() isEditMode: boolean = false;

  Frequency = ScheduleFrequency;
  frequencyValues = Object.values(ScheduleFrequency) as string[];

  public minDate: Date = new Date();
  public startAt: Date = new Date();
  public timezones: string[] = [];

  public selectedTimezone: string = '';
  private previousTimezone: string = '';
  private previousScheduleDateTime: Date | null = null;

  @Output() stateChange = new EventEmitter<{ disableSendNow: boolean; disableSchedule: boolean }>();

  constructor(
    private snackBarService: SnackBarService,
    private timezoneService: TimezoneService,
    private validationService: ScheduleEmailValidationService
  ) { 
    this.timezones = this.timezoneService.getTimeZones();
  }
  ngOnInit(): void {
    this.initializeTimezone();
    this.initializePreviousValues();
    this.emitState(false);
  }

  private initializeTimezone(): void {
    if (this.timezone) {
      this.selectedTimezone = this.timezone;
    } else {
      const defaultTimezone = this.timezoneService.getDefaultTimezone();
      if (defaultTimezone) {
        this.selectedTimezone = defaultTimezone;
      }
    }
  }

  private initializePreviousValues(): void {
    this.previousTimezone = this.selectedTimezone;
    this.previousScheduleDateTime = this.scheduleDateTime ? new Date(this.scheduleDateTime) : null;
  }
  get isNowSelected(): boolean {
    return this.frequency === ScheduleFrequency.Now;
  }

  get isTimezoneSelected(): boolean {
    return this.selectedTimezone && this.selectedTimezone.length > 0;
  }

  getRecipientListTooltip(): string {
    return this.recipientList.join('</br>');
  }
  getScheduleValues(): ScheduleEmailProps {
    return {
      tagList: this.tagList,
      recipientList: this.recipientList,
      frequency: this.frequency,
      scheduleDate: this.scheduleDateTime,
      timezone: this.selectedTimezone
    };
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['frequency']) {
      this.handleFrequencyChange(changes['frequency'].currentValue);
    } else if (changes['scheduleDateTime'] || changes['timezone']) {
      this.handleDateTimeOrTimezoneChange();
    }
  }

  private handleFrequencyChange(newFrequency: ScheduleFrequency): void {
    this.emitState(false);
  }

  private handleDateTimeOrTimezoneChange(): void {
    this.emitState(false);
  }

  onFrequencyChange(newValue: ScheduleFrequency): void {
    if (this.isEditMode && newValue === ScheduleFrequency.Now) {
      return;
    }
    
    this.frequency = newValue;
    this.emitState(false);
  }

  onTimezoneChange(): void {
    if (this.selectedTimezone !== this.previousTimezone) {
      this.previousTimezone = this.selectedTimezone;
    }
  }

  onDateTimeChange(): void {
    if (this.hasDateTimeChanged()) {
      this.updatePreviousDateTime();
    }
  }

  private hasDateTimeChanged(): boolean {
    return this.scheduleDateTime !== this.previousScheduleDateTime;
  }

  private updatePreviousDateTime(): void {
    this.previousScheduleDateTime = this.scheduleDateTime ? new Date(this.scheduleDateTime) : null;
  }

  /**
   * Validates the schedule when the Schedule Email button is clicked.
   * Only validates for recurrent frequencies (Daily, Weekly, Monthly).
   * OneTime and Now frequencies do not require validation.
   * 
   * @returns ValidationState with validation results and proceed information
   */
  validateSchedule(): ValidationState {
    // Only validate for recurrent frequencies (Daily, Weekly, Monthly)
    if (this.frequency === ScheduleFrequency.Now || 
        this.frequency === ScheduleFrequency.OneTime) {
      return {
        isValid: true,
        shouldDisableSchedule: false,
        shouldShowNotification: false,
        shouldProceedAnyway: false
      };
    }

    // Reset warning state to ensure notification shows if validation fails
    this.validationService.resetWarningState();
    
    const validationState = this.validationService.validateAndNotify(
      this.scheduleDateTime,
      this.selectedTimezone,
      this.frequency,
      this.snackBarService
    );

    return validationState;
  }

  private emitState(shouldDisableSchedule: boolean = false): void {
    const disableSendNow = this.frequency !== ScheduleFrequency.Now;
    const disableSchedule = this.frequency === ScheduleFrequency.Now || shouldDisableSchedule;
    this.stateChange.emit({ disableSendNow, disableSchedule });
  }
}
